// Both MyFPO/activitiesApi.js and MyBusiness/activitiesApi.js pass an `api` instance created via
// createApi("myFPO") -- every function below calls fpo directly, never proxied through
// mybusiness's own port. That matters for AGM specifically: fpo builds document URLs from the
// request it receives, and a mybusiness-proxied request would reach fpo as an internal
// server-to-server call, making the returned URLs point at fpo's internal address instead of a
// browser-reachable one.
export const createActivitiesApi = (api, serviceKey) => ({
  // Legal Compliance
  registerLegalCompliance: (fpoId, data) => api.post(`/fpo/${fpoId}/legal`, data),
  // Legal Compliance shares one table across MyFPO and MyBusiness -- serviceKey keeps each
  // service's admin list scoped to its own submissions (see LegalComController.getLegalCompliance).
  getLegalCompliance: (type) => api.get("/fpo/legal", { params: { type, serviceKey } }),
  // Scoped to one tenant's own submissions -- used by the submitter's own "My Requests" list,
  // as opposed to getLegalCompliance's cross-tenant admin view.
  getLegalComplianceByTenant: (fpoId, type) => api.get(`/fpo/${fpoId}/legal`, { params: { type } }),
  updateLegalComplianceStatus: (id, status, response) => api.patch(`/fpo/legal/${id}/status`, { status, response }),

  // Annual Compliance
  registerAnnualCompliance: (fpoId, data) => api.post(`/fpo/${fpoId}/annual`, data),
  getAnnualCompliance: (type) => api.get("/fpo/annual", { params: { type, serviceKey } }),
  getAnnualComplianceByTenant: (fpoId, type) => api.get(`/fpo/${fpoId}/annual`, { params: { type } }),
  updateAnnualComplianceStatus: (id, status, response) => api.patch(`/fpo/annual/${id}/status`, { status, response }),

  // Capacity Building shares one table across MyFPO and MyBusiness -- serviceKey keeps each
  // service's admin list scoped to its own submissions (see CapacityBuildingController.getCapacityBuilding).
  registerCapacityBuilding: (fpoId, data) => api.post(`/fpo/${fpoId}/capacity`, data),
  getCapacityBuilding: (type) => api.get("/fpo/capacity", { params: { type, serviceKey } }),
  getCapacityBuildingByTenant: (fpoId, type) => api.get(`/fpo/${fpoId}/capacity`, { params: { type } }),
  updateCapacityBuildingStatus: (id, status, response) => api.patch(`/fpo/capacity/${id}/status`, { status, response }),

  // Business Plan shares one table across MyFPO and MyBusiness -- serviceKey keeps each
  // service's admin list scoped to its own submissions (see FPOBusinessController.getAllFPOBusinessPlan).
  registerBusiness: (fpoId, data) => api.post(`/fpo/${fpoId}/business`, data),
  getBusiness: (type) => api.get("/fpo/business", { params: { type, serviceKey } }),
  getBusinessByTenant: (fpoId, type) => api.get(`/fpo/${fpoId}/business`, { params: { type } }),
  updateBusinessStatus: (id, status, response) => api.patch(`/fpo/business/${id}/status`, { status, response }),

  // AGM & Board Meetings -- org-scoped: each FPO/Business has its own documents, so every call
  // is addressed to one tenant the same way registerCapacityBuilding/getCapacityBuildingByTenant
  // are. Function names are unchanged, so MyFPO/activitiesApi.js and MyBusiness/activitiesApi.js
  // need no edit to their explicit export lists.
  addAGMBoards: (fpoId, formData) =>
    api.post(`/fpo/${fpoId}/agm`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  getAGMBoards: (fpoId) => api.get(`/fpo/${fpoId}/agm`),
  // AGM & Board documents (AgmDocumentController): many per section, named, dated, archivable.
  getAgmDocuments: (fpoId) => api.get(`/fpo/${fpoId}/agm-documents`),
  // The organisation(s) the signed-in user sees AGM documents of: [{ tenantId, serviceKey, name, admin }]
  getMyAgmOrganisations: () => api.get("/fpo/agm-documents/my-organisations"),
  // formData: section, category, documentDate, files (repeated), names (repeated, one per file)
  uploadAgmDocuments: (fpoId, formData) =>
    api.post(`/fpo/${fpoId}/agm-documents`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  // data: any of name, category, documentDate, archived
  updateAgmDocument: (fpoId, id, data) => api.patch(`/fpo/${fpoId}/agm-documents/${id}`, data),

  // Dashboard / Reports
  getMembershipDashboard: (fpoId, params) => api.get(`/fpo/${fpoId}/dashboard/membership`, { params }),
  getShareCapitalDashboard: (fpoId, params) => api.get(`/fpo/${fpoId}/dashboard/share-capital`, { params }),
  getTurnoverDashboard: (fpoId, params) => api.get(`/fpo/${fpoId}/dashboard/turnover`, { params }),
});
